import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import clsx from 'clsx'
import { useSearchParam } from 'react-use'
import useSWR from 'swr'

import { ShowcaseFeedDocument, ShowcaseOrder } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { normalizeImage } from '@/lib/contentful/normalizers/image'
import { serializeCacheKey, useSlug } from '@/lib/contentful/utils'

import startsWithHttps from '@/utils/string/startsWithHttp'

import { Pagination } from '@/components/Pagination'

import { ShowcaseCard } from '../ShowcaseCard'
import { getShowcaseCategories } from '../helpers/getShowcaseCategories'
import { ShowcasePriorityCategories } from './ShowcaseFeed.makeswift'

type Props = {
	className?: string
	nextText?: string
	prevText?: string
	action?: string
	caseStudy?: string
	partnerCaseStudy?: string
} & ShowcasePriorityCategories

export const LIMIT = 200

export function ShowcaseFeed({
	className,
	action,
	nextText,
	prevText,
	caseStudy,
	partnerCaseStudy,
	...rest
}: Props) {
	const { locale, isPreview } = useRouter()
	const pageSearchParam = useSearchParam('page')
	const [page, setPage] = useState(
		typeof pageSearchParam === 'string' ? Number(pageSearchParam) : 1
	)

	const { data: categories } = useSWR(
		serializeCacheKey('showcaseCategory', { limit: 100, locale }),
		async () => await getShowcaseCategories(0, 100, locale)
	)

	const baseCategory = categories?.find((item) => item?.slug_en_US === 'showcase')
	const slug = useSlug() ?? baseCategory?.slug
	const en_USCategory = categories?.find((item) => item.slug === slug)
		?.slug_en_US as keyof ShowcasePriorityCategories

	const { data } = useSWR(
		serializeCacheKey('showcaseFeed', {
			category: slug,
			limit: LIMIT,
			skip: 0,
			locale
		}),
		() =>
			client({ preview: isPreview }).request(ShowcaseFeedDocument, {
				where: { category: { slug }, image_exists: true },
				order: [ShowcaseOrder.TitleAsc],
				limit: LIMIT,
				skip: 0,
				preview: isPreview,
				locale
			})
	)

	// Scrolls user to top of page when navigating to a new page
	useEffect(() => {
		if (page) {
			global.scrollTo({ top: 0, behavior: 'smooth' })
		}
	}, [page])

	const feed = data?.showcaseCollection?.items
		?.sort((a, b) => {
			const ai = (rest[en_USCategory] ?? []).findIndex((id) => id && id === a?.sys.id)
			const bi = (rest[en_USCategory] ?? []).findIndex((id) => id && id === b?.sys.id)
			if (ai > -1 && bi === -1) return -1
			else if (ai === -1 && bi > -1) return 1
			else return ai - bi
		})
		.slice((page - 1) * 50, page * 50)

	const category = categories?.find((item) => item?.slug === slug)
	return (
		<div className={clsx(className, 'grid gap-8')}>
			<h1 id="showcase-title" className="text-h1 text-[41px] leading-[50px] m-0">
				{category?.name}
			</h1>
			<div data-testid="showcase-grid" className="grid grid-cols-1 md:grid-cols-2 gap-8">
				{feed?.map((item) => {
					const isStudyExternal = startsWithHttps(item?.caseStudy?.url || '')
					const title = item?.title
					const externalUrl = item?.externalUrl?.url
					const image = normalizeImage(item?.image)
					if (!title || !externalUrl) return null
					return (
						<ShowcaseCard
							data-testid="showcase-card"
							key={item.slug}
							title={title}
							imageSrc={image}
							imageAlt={image?.alt ?? title}
							linkURL={{ href: externalUrl, target: '_blank' }}
							caseStudyURL={{
								href: item.caseStudy?.url,
								target: '_blank',
								isExternal: isStudyExternal,
								label: isStudyExternal ? partnerCaseStudy : caseStudy
							}}
							variant="dark"
							bgColor="#ffffff"
							className="border-[1px] border-solid border-gray-1000 rounded-b-2xl"
						/>
					)
				})}
			</div>
			<Pagination
				total={data?.showcaseCollection?.total ?? 50}
				limit={50}
				nextText={nextText}
				prevText={prevText}
				current={page}
				handlePageChange={setPage}
			/>
		</div>
	)
}
